import {
  getAdditionalLoanInformationConfig,
  getDocumentVerificationTypesConfig,
  getLoanDetailsConfig,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.config';
import _ from 'lodash';

import { LienPriorityType, TANGIBLE_NET_BENEFIT_STATES } from 'constants/loanApplication';
import { getConditionalObject } from 'utils/data';
import { checkIsRefinanceCashOutSubflow, checkIsRefinanceFlow } from 'utils/loanApplication';
import { checkIsPendingChangeHasDifference } from 'utils/pendingChanges';

import {
  ESCROW_MASTER_PATH,
  LOAN_AND_LOCK_HOUSING_FINANCE_AGENCY_FIELD_NAME,
  LOAN_AND_LOCK_LIVE_SELECT_FIELD_NAMES,
} from './LoanEditorCustomFormDrawer.constants';
import { flattenGroupFields } from './LoanEditorCustomFormDrawer.entity.utils';
import {
  buildEscrowConfigInputs,
  decorateEscrowGroups,
} from './LoanEditorCustomFormDrawer.escrow.utils';

export const getLoanInfoGroups = ({
  loanDetailsConfigInputs,
  additionalLoanInformationConfigInputs,
  escrowConfigInputs,
}) => [
  ...decorateEscrowGroups({
    groups: getLoanDetailsConfig(loanDetailsConfigInputs).groups,
    escrowConfigInputs,
  }),
  ...getAdditionalLoanInformationConfig(additionalLoanInformationConfigInputs).groups,
  ...getDocumentVerificationTypesConfig().groups,
];

export const buildMortgageLienPriorityOptions = ({ additionalMortgages, lienPriorityOptions }) => {
  const busyLienPriorityTypes = Object.values(additionalMortgages ?? {}).reduce(
    (acc, { lienPriorityType }) => {
      const value = lienPriorityType?.value ?? lienPriorityType;

      if (value && value !== LienPriorityType.OTHER) {
        acc.push(value);
      }

      return acc;
    },
    [],
  );

  return lienPriorityOptions.filter(
    ({ value }) => value === LienPriorityType.OTHER || !busyLienPriorityTypes.includes(value),
  );
};

export const buildInsuranceEscrowIds = escrowSettings => {
  const escrowed = escrowSettings?.insuranceEscrowSettings?.insuranceImpoundsEscrowed ?? [];

  return Array.isArray(escrowed)
    ? escrowed.map(({ applicationInsuranceId }) => applicationInsuranceId)
    : [];
};

export const pickLoanAndLockLiveValues = ({ formState, formPendingChanges }) => {
  const checkIsEdited = path => checkIsPendingChangeHasDifference(_.get(formPendingChanges, path));

  return {
    ...LOAN_AND_LOCK_LIVE_SELECT_FIELD_NAMES.filter(checkIsEdited).reduce(
      (acc, fieldName) => ({ ...acc, [fieldName]: _.get(formState, fieldName)?.value ?? null }),
      {},
    ),
    ...getConditionalObject(checkIsEdited(LOAN_AND_LOCK_HOUSING_FINANCE_AGENCY_FIELD_NAME), () => ({
      isOtherHousingFinanceAgency: !!_.get(
        formState,
        LOAN_AND_LOCK_HOUSING_FINANCE_AGENCY_FIELD_NAME,
      )?.isOther,
    })),
    ...getConditionalObject(checkIsEdited(ESCROW_MASTER_PATH), () => ({
      isEscrowed: !!_.get(formState, ESCROW_MASTER_PATH),
    })),
  };
};

const getStoredLoanAndLockValues = applicationData => ({
  loanPurpose: applicationData.loanPurpose,
  refinancePurpose: applicationData.refinancePurpose,
  tangibleNetBenefitReason: applicationData.tangibleNetBenefitReason,
  nationalHousingActSection: applicationData.nationalHousingActSection,
  isOtherHousingFinanceAgency: !!applicationData.housingFinanceAgencyOtherDescription,
  isEscrowed: !!_.get(applicationData, ESCROW_MASTER_PATH),
});

const buildLoanInfoConfigInputs = ({
  values,
  mortgageType,
  loanWorkflow,
  subjectPropertyState,
  buyDownOptions,
  insuranceEscrowIds,
  isLoanLocked,
  isLoanNoPpeEngineType,
  housingFinanceAgenciesOptions,
  investorFeatureCodesOptions,
  leadSourcesOptions,
  mortgageLienPriorityOptions,
  programIdentifiersOptions,
}) => {
  const {
    loanPurpose,
    refinancePurpose,
    tangibleNetBenefitReason,
    nationalHousingActSection,
    isOtherHousingFinanceAgency,
  } = values;

  return {
    loanDetailsConfigInputs: {
      loanPurpose,
      shouldShowTotalLoanAmount: true,
      buyDownOptions,
      mortgageLienPriorityOptions,
      mortgageType,
      insuranceEscrowIds,
      isRefinanceCashOutSubflow: checkIsRefinanceCashOutSubflow({ refinancePurpose }),
      shouldRecalculateCashOutAmount: false,
      isTangibleNetBenefitVisible:
        checkIsRefinanceFlow(loanPurpose) &&
        TANGIBLE_NET_BENEFIT_STATES.includes(subjectPropertyState),
      tangibleNetBenefitReason,
      isLoanLocked,
    },
    additionalLoanInformationConfigInputs: {
      programIdentifiersOptions,
      investorFeatureCodesOptions,
      leadSourcesOptions,
      isLoanNoPpeEngineType,
      loanWorkflow,
      mortgageType,
      loanPurpose,
      housingFinanceAgenciesOptions,
      isOtherHousingFinanceAgency,
      isLoanLocked,
      nationalHousingActSection,
    },
  };
};

const collectLoanInfoFieldPaths = loanInfoConfigInputs =>
  new Set(
    flattenGroupFields(getLoanInfoGroups(loanInfoConfigInputs))
      .flatMap(({ nameInModel, combinedFields }) => [
        nameInModel,
        ..._.map(combinedFields, 'nameInModel'),
      ])
      .filter(Boolean),
  );

const collectHiddenLoanInfoFieldPaths = ({ storedConfigInputs, liveConfigInputs }) => {
  const liveFieldPaths = collectLoanInfoFieldPaths(liveConfigInputs);

  return new Set(
    [...collectLoanInfoFieldPaths(storedConfigInputs)].filter(path => !liveFieldPaths.has(path)),
  );
};

export const buildLoanAndLockContext = ({
  isEnabled,
  applicationData,
  liveValues = {},
  escrowRequirement,
  impoundTypes,
  onEscrowMasterChange,
  ...configOptions
}) => {
  const storedValues = getStoredLoanAndLockValues(applicationData);
  const escrowConfigInputs = buildEscrowConfigInputs({
    escrowSettings: applicationData.escrowSettings,
    requireEscrow: applicationData.requireEscrow,
    mortgageType: applicationData.mortgageType,
    escrowRequirement,
    impoundTypes,
    isEscrowed: { ...storedValues, ...liveValues }.isEscrowed,
    onMasterChange: onEscrowMasterChange,
  });
  const sharedConfigOptions = {
    ...configOptions,
    mortgageType: applicationData.mortgageType,
    loanWorkflow: applicationData.loanWorkflow,
  };
  const liveConfigInputs = buildLoanInfoConfigInputs({
    ...sharedConfigOptions,
    values: { ...storedValues, ...liveValues },
  });

  return {
    isEnabled,
    ...liveConfigInputs,
    escrowConfigInputs,
    hiddenFieldPaths: isEnabled
      ? collectHiddenLoanInfoFieldPaths({
          storedConfigInputs: buildLoanInfoConfigInputs({
            ...sharedConfigOptions,
            values: storedValues,
          }),
          liveConfigInputs,
        })
      : new Set(),
  };
};
